import { useEffect, useRef, useState } from 'react';
import { MoreHorizontal } from 'lucide-react';
import { cn } from '@/lib/utils/cn';

/**
 * RegisterCell — inline-edit cell for the register grid.
 *
 * Two presentation modes:
 *   - **Plain (default)**: shows the value; click → enters edit mode.
 *   - **Edit mode**: focused `<input>`; Enter or blur commits, Esc cancels.
 *
 * A hover-revealed "..." button (NOT right-click) opens the cell context
 * menu. The ticket calls this out explicitly:
 *   "Cell context menu ('...' button on hover, NOT right-click for a11y)
 *    → 'Override' → opens override modal (T-0113 will own the modal; for
 *    now, trigger via data-open-override attribute + the modal context
 *    shipped in T-0093)."
 *
 * v1 wiring: the "..." button carries `data-open-override="<entryId>"` so
 * the modal context (T-0113) can pick it up the same way the prototype
 * trips its modal openers via delegated `data-open-*` listeners on the
 * shell. On override save, the re-eval tray auto-opens — that flow is
 * owned by T-0113 too; this ticket just ensures the trigger exists.
 *
 * The "..." button is hidden when:
 *   - `readOnly` is true (snapshot / client-viewer scope), or
 *   - the cell is currently in edit mode (cleaner focus).
 *
 * Live (Yjs) cells bypass this component entirely — `<YjsCell>` is rendered
 * in its place by the row when the relay is connected. That keeps the
 * Yjs T-0032 contract intact (zero regression in live-edit behavior).
 */

export interface RegisterCellProps {
  /** Cell value to display + edit. */
  value: string;
  /** Commit handler — called on Enter or input blur if the value changed. */
  onCommit: (next: string) => void;
  /** Hide all edit affordances. */
  readOnly?: boolean;
  /** Placeholder for empty cells. */
  placeholder?: string;
  /** Register-entry id used as the `data-open-override` payload. */
  entryId: string;
  /** Cell key (`vendor_claim`, `avero_verification`, etc.) — surfaced on the
   *  override button so the modal can scope which axis is being overridden. */
  cellKey: string;
  /** Whether a mutation is in flight on this cell (drives the spinner). */
  saving?: boolean;
  /** Extra classes — passed straight through to the outer container. */
  className?: string;
}

export function RegisterCell({
  value,
  onCommit,
  readOnly = false,
  placeholder,
  entryId,
  cellKey,
  saving = false,
  className,
}: RegisterCellProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Keep the draft in sync with upstream changes (e.g. a peer's commit
  // through the REST path while this user is *not* in edit mode).
  useEffect(() => {
    if (!editing) setDraft(value);
  }, [value, editing]);

  // Focus + select the input when we enter edit mode.
  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  const commit = () => {
    setEditing(false);
    if (draft !== value) onCommit(draft);
  };

  const cancel = () => {
    setEditing(false);
    setDraft(value);
  };

  if (editing && !readOnly) {
    return (
      <div className={cn('group relative w-full', className)}>
        <input
          ref={inputRef}
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              commit();
            } else if (e.key === 'Escape') {
              e.preventDefault();
              cancel();
            }
          }}
          placeholder={placeholder}
          className="block h-7 w-full rounded-card border border-accent-amber-600 bg-paper-0 px-2 text-body-sm text-ink-900 outline-none focus:ring-2 focus:ring-accent-amber-600/40"
        />
      </div>
    );
  }

  // Plain mode — click to edit, hover reveals the "..." override trigger.
  return (
    <div
      className={cn(
        'group relative flex min-h-[28px] items-center gap-2 rounded-card px-2 py-1',
        readOnly ? 'cursor-default' : 'cursor-text hover:bg-paper-100',
        className,
      )}
      onClick={() => {
        if (!readOnly) setEditing(true);
      }}
      // Keyboard-equivalent of click: Enter / Space on the cell enters edit.
      // The cell is a focusable region (tabIndex={0}) so keyboard users can
      // reach it without going through the "..." button.
      tabIndex={readOnly ? -1 : 0}
      onKeyDown={(e) => {
        if (readOnly) return;
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          setEditing(true);
        }
      }}
      role={readOnly ? undefined : 'button'}
      aria-label={readOnly ? undefined : `Edit ${cellKey}`}
    >
      <span
        className={cn(
          'block min-w-0 flex-1 truncate text-body-sm',
          value ? 'text-ink-900' : 'text-ink-300',
        )}
      >
        {value || placeholder || ''}
      </span>

      {saving ? (
        <span aria-hidden="true" className="text-meta text-ink-500">
          Saving…
        </span>
      ) : null}

      {/* Hover-only override trigger. NOT right-click — the ticket calls out
          the a11y reason (keyboard users can't open a right-click menu).
          `data-open-override` + the cell key give T-0113's modal everything
          it needs to scope the override capture. */}
      {readOnly ? null : (
        <button
          type="button"
          aria-label={`Open override menu for ${cellKey}`}
          data-open-override={entryId}
          data-override-cell={cellKey}
          onClick={(e) => {
            // Don't bubble into the cell's own click-to-edit.
            e.stopPropagation();
          }}
          className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-chip text-ink-500 opacity-0 transition-opacity hover:bg-ink-100 hover:text-ink-900 focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-amber-600 group-hover:opacity-100"
        >
          <MoreHorizontal className="h-3.5 w-3.5" aria-hidden="true" />
        </button>
      )}
    </div>
  );
}
